import type { ClassNames, MantineStyleSystemProps } from '@mantine/core';
import { createStyles } from '@mantine/core';
import { Button } from '@repo/foundations';
import { width } from '@repo/theme/primitives';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import AddMonitorModal from '../AddMonitorV2/AddMonitorModal/AddMonitorModal';
import { useStyles as useAddMonitorStyles } from '../AddMonitorV2/AddMonitorModal/AddMonitorModal.styles';
import { ADD_MONITOR_MODAL_ID } from '../AddMonitorV2/AddMonitorModal/constants';
import { openModal } from '../ModalManager';
import type { AddMonitorStoreProviderProps } from './context';
import { AddMonitorStoreProvider } from './context';

const useStyles = createStyles({
	root: {},
});

type AddMonitorButtonStylesNames = 'root';

export interface AddMonitorProps
	extends MantineStyleSystemProps,
		Omit<AddMonitorStoreProviderProps, 'children'> {
	withIcon?: boolean;
	customTarget?: ReactNode;
	className?: string;
	classNames?: ClassNames<AddMonitorButtonStylesNames>;
	/** Entity ID to filter to only upstream entities of (for V2 monitor creation flow) */
	upstreamOf?: string;
}

function AddMonitorButton({
	initialTable,
	initialIntegration,
	initialMetricType,
	initialSearchTerm,
	upstreamOf,
	withIcon = false,
	className,
	classNames: classNamesProp,
	customTarget,
	...others
}: AddMonitorProps) {
	const { classes, cx } = useStyles();
	const { classes: addMonitorClasses } = useAddMonitorStyles();

	const classNames = useMemo(
		() => ({
			root: cx(classes.root, classNamesProp?.root),
		}),
		[classes, cx, classNamesProp]
	);

	const handleOpen = () => {
		openModal({
			modalId: ADD_MONITOR_MODAL_ID,
			title: 'Select monitor type',
			children: (
				<AddMonitorModal
					initialTable={initialTable}
					initialIntegration={initialIntegration}
					upstreamOf={upstreamOf}
				/>
			),
			size: width.xxl,
			radius: 'lg',
			classNames: {
				body: addMonitorClasses.body,
			},
		});
	};

	return (
		<AddMonitorStoreProvider
			initialTable={initialTable}
			initialIntegration={initialIntegration}
			initialMetricType={initialMetricType}
			initialSearchTerm={initialSearchTerm}
		>
			{customTarget ?? (
				<Button
					className={cx(classNames.root, className)}
					onClick={handleOpen}
					leftIconName={withIcon ? 'activity' : undefined}
					variant="primary"
					{...others}
				>
					Create monitor
				</Button>
			)}
		</AddMonitorStoreProvider>
	);
}

const ObservedAddMonitorButton = observer(AddMonitorButton);
export default ObservedAddMonitorButton;
